import { SYSTEM_EVENTS, SYSTEM_NAME_SPACE } from '../constant.ts';
import { IEvents } from '../events/events.ts';
import { IAccountModel, IBalanceLimit } from '../models/account/IAccount.ts';
import { IBalance } from '../models/balance/IBalance.ts';
import { getCurrentDate } from '../utils/date/dateUtils.ts';
import { ITransactionCreate } from '../utils/db/types.ts';
import {
    currencyFormatter,
    currencyFormatterEvent,
    numberFormatter,
    onlyNumbersFormatter,
} from '../utils/formatters/inputFormatter.ts';
import { IMainView } from '../views/types.ts';

import { IDayLimitPresenter } from './types.ts';

export class DayLimitPresenter implements IDayLimitPresenter {
    private readonly events: IEvents;
    private readonly accountModel: IAccountModel;
    private readonly view: IMainView;
    private cardNode: HTMLElement | undefined = undefined;

    constructor(accountModel: IAccountModel, view: IMainView, events: IEvents) {
        this.accountModel = accountModel;
        this.view = view;
        this.events = events;

        this.events.on(SYSTEM_EVENTS.BALANCE_UPDATED, () => {
            this.balanceUpdate();
            this.commentUpdate();
        });
    }

    public init() {
        const balance: IBalance = this.accountModel.getBalance();

        const cardBlock = document.createElement('div');
        cardBlock.classList.add('card-block');

        const cardDescription = document.createElement('div');
        cardDescription.classList.add('card__title');

        const cardDescSpan1 = document.createElement('h3');
        cardDescSpan1.classList.add('card__title-left');
        cardDescSpan1.textContent = 'На сегодня доступно';

        cardDescription.appendChild(cardDescSpan1);

        cardBlock.appendChild(cardDescription);

        const cardBody = document.createElement('div');
        cardBody.classList.add('card__body');

        const balanceBlock = document.createElement('div');
        balanceBlock.classList.add('balance-block');

        const balanceBlockH1 = document.createElement('h1');
        balanceBlockH1.classList.add('balance-block__balance');

        const balanceBlockSpan1 = document.createElement('span');
        balanceBlockSpan1.classList.add('balance-block__balance--wasted');
        balanceBlockSpan1.textContent = currencyFormatter(balance.wasted);

        const balanceBlockSpan2 = document.createElement('span');
        balanceBlockSpan2.classList.add('balance-block__balance--available');
        balanceBlockSpan2.textContent = currencyFormatter(balance.dayLimit);

        balanceBlockH1.appendChild(balanceBlockSpan1);
        balanceBlockH1.appendChild(balanceBlockSpan2);

        balanceBlock.appendChild(balanceBlockH1);

        cardBody.appendChild(balanceBlock);

        const wastedComment = document.createElement('p');
        wastedComment.classList.add('balance-block__wasted-comment');

        cardBody.appendChild(wastedComment);

        const form = document.createElement('form');
        form.name = 'form-wasted';
        form.classList.add('form', 'form--day-limit');

        /* label.input */
        const balanceLabel = document.createElement('label');
        balanceLabel.classList.add('input');

        const balanceTitle = document.createElement('span');
        balanceTitle.classList.add('input__title', 'caption');
        balanceTitle.textContent = 'Введите трату';

        const balanceInput = document.createElement('input');
        balanceInput.name = 'wasted';
        balanceInput.classList.add('input__input');
        balanceInput.placeholder = '0 ₽';

        balanceInput.addEventListener('input', onlyNumbersFormatter);
        balanceInput.addEventListener('change', currencyFormatterEvent);

        balanceLabel.append(balanceTitle, balanceInput);

        const balanceChangeButton = document.createElement('button');
        balanceChangeButton.classList.add('btn', 'btn--primary', 'balance-block__change-button');
        balanceChangeButton.textContent = 'Сохранить';
        balanceChangeButton.type = 'submit';

        form.append(balanceLabel, balanceChangeButton);
        cardBody.appendChild(form);

        cardBlock.appendChild(cardBody);

        form.addEventListener('submit', this.handleSubmitForm.bind(this));

        this.cardNode = cardBlock;

        this.balanceUpdate();
        this.commentUpdate();

        this.view.addChildWithKey(SYSTEM_NAME_SPACE.DAY_LIMIT_BLOCK, cardBlock);

        this.destroyData = () => {
            this.cardNode?.classList.toggle('card-block--hidden');

            this.view.unmount(SYSTEM_NAME_SPACE.DAY_LIMIT_BLOCK);

            balanceInput.removeEventListener('input', onlyNumbersFormatter);
            balanceInput.removeEventListener('change', currencyFormatterEvent);
            form.removeEventListener('submit', this.handleSubmitForm.bind(this));
        };
    }

    public destroy() {
        this.destroyData();
    }

    private async handleSubmitForm(event: Event) {
        event.preventDefault();

        const form: HTMLFormElement | null = document.forms.namedItem('form-wasted');

        if (!form) {
            throw new Error('Form is not defined');
        }

        const balanceInput: Element | RadioNodeList | null = form.elements.namedItem('wasted');

        if (!balanceInput || !(balanceInput instanceof HTMLInputElement)) {
            throw new Error('Wasted is not defined');
        }

        if ('value' in balanceInput) {
            const newTransaction: ITransactionCreate = {
                value: numberFormatter(balanceInput.value),
                date: getCurrentDate().toISOString(),
            };

            await this.accountModel.addTransaction(newTransaction);

            balanceInput.value = '';
            balanceInput.placeholder = '0 ₽';
        }
    }

    private balanceUpdate(): void {
        if (!this.cardNode) {
            throw new Error('Card node is not defined');
        }

        const balanceBlockWaste = this.cardNode.querySelector('.balance-block__balance--wasted');
        const balanceBlockAviable = this.cardNode.querySelector('.balance-block__balance--available');

        if (!balanceBlockWaste || !balanceBlockAviable) {
            throw new Error('Balance block is not defined');
        }

        const remainedBalanceDay = this.accountModel.getBalanceLimit();

        balanceBlockAviable.textContent = currencyFormatter(remainedBalanceDay.limit);
        balanceBlockWaste.textContent = currencyFormatter(remainedBalanceDay.remained);
        balanceBlockWaste.classList.remove();

        if (remainedBalanceDay.remained >= 0) {
            balanceBlockWaste.classList.add('balance-block__balance--remained--success');
        } else {
            balanceBlockWaste.classList.add('balance-block__balance--remained--error');
        }
    }

    private commentUpdate(): void {
        if (!this.cardNode) {
            throw new Error('Card node is not defined');
        }

        const commentBlock = this.cardNode.querySelector('.balance-block__wasted-comment');

        if (!commentBlock) {
            throw new Error('Comment block is not defined');
        }

        const balanceLimit: IBalanceLimit = this.accountModel.getBalanceLimit();

        if (balanceLimit.inLimit) {
            commentBlock.textContent = '🎉 Отлично справились — сегодня вы в пределах лимита!';
        } else {
            commentBlock.textContent = '❌ Вы превысили лимит на сегодня!';
        }
    }

    private destroyData: () => void = () => {};
}
